import React, { useEffect, useState, useRef } from 'react';
import { MoveHorizontal } from 'lucide-react';
import { useLanguage } from '../../i18n/LanguageContext';
interface BeforeAfterSliderProps {
  imageSrc: string;
}
export function BeforeAfterSlider({
  imageSrc
}: BeforeAfterSliderProps) {
  const {
    t
  } = useLanguage();
  const [sliderPosition, setSliderPosition] = useState(50);
  const [isDragging, setIsDragging] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const handleMove = (clientX: number) => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const x = Math.max(0, Math.min(clientX - rect.left, rect.width));
    const percent = x / rect.width * 100;
    setSliderPosition(percent);
  };
  const onMouseMove = (e: MouseEvent) => {
    if (!isDragging) return;
    handleMove(e.clientX);
  };
  const onTouchMove = (e: TouchEvent) => {
    if (!isDragging) return;
    handleMove(e.touches[0].clientX);
  };
  const stopDragging = () => {
    setIsDragging(false);
  };
  useEffect(() => {
    if (isDragging) {
      window.addEventListener('mousemove', onMouseMove);
      window.addEventListener('mouseup', stopDragging);
      window.addEventListener('touchmove', onTouchMove, {
        passive: false
      });
      window.addEventListener('touchend', stopDragging);
    }
    return () => {
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', stopDragging);
      window.removeEventListener('touchmove', onTouchMove);
      window.removeEventListener('touchend', stopDragging);
    };
  }, [isDragging]);
  return <div ref={containerRef} className="relative w-full max-w-md mx-auto aspect-[3/4] sm:aspect-[4/5] bg-gray-100 rounded-2xl overflow-hidden select-none cursor-ew-resize shadow-xl" onMouseDown={e => {
    setIsDragging(true);
    handleMove(e.clientX);
  }} onTouchStart={e => {
    setIsDragging(true);
    handleMove(e.touches[0].clientX);
  }} data-id="element-190">
      {/* BEFORE IMAGE (Bottom Layer) */}
      <div className="absolute inset-0 w-full h-full" data-id="element-191">
        <img src={imageSrc} alt="Before" className="w-full h-full object-cover" style={{
        transform: 'scaleX(-1)'
      }} data-id="element-192" />
        <div className="absolute top-4 left-4 bg-black/50 backdrop-blur-sm text-white px-3 py-1 rounded-full text-xs font-medium tracking-wider" data-id="element-193">
          {t('faceAnalysis.results.before')}
        </div>
      </div>

      {/* AFTER IMAGE (Top Layer, Clipped) */}
      <div className="absolute inset-0 w-full h-full border-r-2 border-white" style={{
      clipPath: `polygon(0 0, ${sliderPosition}% 0, ${sliderPosition}% 100%, 0 100%)`
    }} data-id="element-194">
        <img src={imageSrc} alt="After" className="w-full h-full object-cover filter contrast-105 brightness-105 saturate-105" style={{
        transform: 'scaleX(-1)'
      }} data-id="element-195" />

        {/* Simulated Treatment Markers (Only visible on After side) */}
        <div className="absolute inset-0 pointer-events-none" data-id="element-196">
          {/* Cheeks */}
          <div className="absolute top-[45%] left-[30%] w-3 h-3 bg-brand-copper rounded-full shadow-[0_0_10px_#c79268] animate-pulse" data-id="element-197"></div>
          <div className="absolute top-[45%] right-[30%] w-3 h-3 bg-brand-copper rounded-full shadow-[0_0_10px_#c79268] animate-pulse" data-id="element-198"></div>

          {/* Nasolabial */}
          <div className="absolute top-[60%] left-[40%] w-2 h-2 bg-brand-lightCopper rounded-full shadow-[0_0_8px_#e6b89c]" data-id="element-199"></div>
          <div className="absolute top-[60%] right-[40%] w-2 h-2 bg-brand-lightCopper rounded-full shadow-[0_0_8px_#e6b89c]" data-id="element-200"></div>

          {/* Lips */}
          <div className="absolute top-[70%] left-1/2 -translate-x-1/2 w-4 h-2 bg-pink-400/50 rounded-full blur-[2px]" data-id="element-201"></div>
        </div>

        <div className="absolute top-4 right-4 bg-brand-copper/80 backdrop-blur-sm text-white px-3 py-1 rounded-full text-xs font-medium tracking-wider" data-id="element-202">
          {t('faceAnalysis.results.after')}
        </div>
      </div>

      {/* SLIDER HANDLE */}
      <div className="absolute top-0 bottom-0 w-1 bg-white cursor-ew-resize z-10" style={{
      left: `${sliderPosition}%`,
      transform: 'translateX(-50%)'
    }} data-id="element-203">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-8 h-8 bg-white rounded-full shadow-lg flex items-center justify-center text-brand-copper" data-id="element-204">
          <MoveHorizontal size={16} data-id="element-205" />
        </div>
      </div>
    </div>;
}